// @flow
import React, { Fragment, useEffect } from 'react';
import { Form, useFormikContext } from 'formik';
import classNames from 'classnames';
import moment from 'moment';

import { t } from 'Src/i18n';
import {
  type FormProducts,
  type FormFieldOption,
  type ProductOption,
} from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { Button } from 'Components/frameworks/button/Button';
import { FormikInput as Field } from 'Components/frameworks/formik/FormikInput';
import { Notification } from 'Components/frameworks/notification/Notification';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { COLORS } from 'Components/utils/color/colors';
import { momentFromApiDate } from 'Utils/date/apiDate';

import { ADULTS, KIDS, MIN_FORM_ADULTS, MIN_FORM_KIDS } from './PrebookingForm.constants';
import {
  type FormCompanion,
  type InitialFormData,
  type FormCivilities,
  getClosingDatesRanges,
} from './PrebookingForm.utils';
import { PrebookingFormCompanion } from './PrebookingFormCompanion';

type NewsletterOptin = null | {
  title: string,
  description: string,
};

export type PrebookingFormBaseProps = {
  adultsOptions: Array<FormFieldOption>,
  agencies: Array<FormFieldOption>,
  civilities: FormCivilities,
  dateFormat: string,
  departureDateEnd: Date,
  departureDateStart: Date,
  hasSeller: boolean,
  hasUnicodeFields: boolean,
  hasUser: boolean,
  kidsOptions: Array<FormFieldOption>,
  newsletterOptins: {
    optinEmail: NewsletterOptin,
    optinPhone: NewsletterOptin,
    optinSms: NewsletterOptin,
    optinProfile: NewsletterOptin,
  },
  productOptions: Array<ProductOption>,
  products: FormProducts,
};

export type PrebookingFormProps = {
  ...$Exact<PrebookingFormBaseProps>,
  language: string,
  initialFormData: InitialFormData,
};

const DEFAULT_COMPANION: FormCompanion = {
  birthdate: null,
  civility: '',
  firstName: '',
  lastName: '',
};

export function PrebookingForm({
  adultsOptions,
  agencies,
  civilities,
  dateFormat,
  departureDateEnd,
  departureDateStart,
  hasSeller,
  hasUnicodeFields,
  hasUser,
  initialFormData,
  kidsOptions,
  language,
  newsletterOptins,
  productOptions,
  products,
}: PrebookingFormProps) {
  const { isValid, values, setValues, isSubmitting } = useFormikContext();

  // CXO/Auth: update traveler information
  useEffect(
    () => {
      setValues({ ...values, ...initialFormData });
    },
    [initialFormData],
  );

  useEffect(
    () => {
      const departureDate = values.departureDate.from;
      if (
        values.productId &&
        departureDate &&
        !products[values.productId]?.openingDates.some(({ opening, closing }) =>
          moment(departureDate).isBetween(momentFromApiDate(opening), momentFromApiDate(closing)),
        )
      ) {
        setValues({
          ...values,
          departureDate: {
            from: null,
            to: null,
          },
        });
      }
    },
    [values.productId],
  );

  const { optinEmail, optinPhone, optinSms, optinProfile } = newsletterOptins;

  const { productId, accommodationCategory, adults, adultsNumber, kids, kidsNumber } = values;
  const product = productId ? products[productId] : null;
  const minAuthorizedAge = product ? product.minAuthorizedAge : null;
  const durationOptions = product ? product.durations : [];
  const transportOptions = product ? product.transports : [];
  const accommodationCategoryOptions = product ? product.accommodations : [];
  const { types: accommodationOptions = [] } =
    product && accommodationCategory
      ? product.accommodations.find(a => a.value === accommodationCategory) || {}
      : {};

  const adultsInt = parseInt(adultsNumber, 10);
  const kidsInt = parseInt(kidsNumber, 10);
  const hasAddtionnalTravelers = adultsInt > MIN_FORM_ADULTS || kidsInt > MIN_FORM_KIDS;

  const handleAdultsNumber = e => {
    const updatedAdultsNumber = e.target.value;
    const updatedAdultsInt = parseInt(updatedAdultsNumber, 10);
    const newAdults = updatedAdultsInt - adultsInt;
    const updatedAdults =
      newAdults > 0
        ? [
            ...adults,
            ...Array(newAdults)
              .fill(null)
              .map(() => DEFAULT_COMPANION),
          ]
        : adults.slice(0, updatedAdultsInt - MIN_FORM_ADULTS);
    setValues({
      ...values,
      adultsNumber: updatedAdultsNumber,
      adults: updatedAdults,
    });
  };

  const handleKidsNumber = e => {
    const updatedKidsNumber = e.target.value;
    const updatedKidsInt = parseInt(updatedKidsNumber, 10);
    const newKids = updatedKidsInt - kidsInt;
    const updatedKids =
      newKids > 0
        ? [
            ...kids,
            ...Array(newKids)
              .fill(null)
              .map(() => DEFAULT_COMPANION),
          ]
        : kids.slice(0, updatedKidsInt);
    setValues({
      ...values,
      kidsNumber: updatedKidsNumber,
      kids: updatedKids,
    });
  };

  const sortedOpeningDates = [...(product?.openingDates || [])].sort(
    (date1, date2) => Number(date1.opening) - Number(date2.opening),
  );

  const disabledDays = getClosingDatesRanges(sortedOpeningDates);

  const firstOpeningDate = sortedOpeningDates[0]?.opening;

  const minDate =
    firstOpeningDate && momentFromApiDate(firstOpeningDate).isAfter(moment(departureDateStart))
      ? momentFromApiDate(firstOpeningDate).toDate()
      : departureDateStart;

  const lastClosingDate = sortedOpeningDates[sortedOpeningDates.length - 1]?.closing;

  const maxDate =
    lastClosingDate && moment(departureDateEnd).isAfter(momentFromApiDate(lastClosingDate))
      ? momentFromApiDate(lastClosingDate).toDate()
      : departureDateEnd;

  const MandatoryFieldsNote = () => (
    <p className="m-0 mb-3 px-2 text-small">{t('prebookingForm.mandatoryFieldsInformation')}</p>
  );

  return (
    <Form id="prebooking-form-form" data-testid="prebooking-form-form">
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('prebookingForm.stay.title')}
      </Title>
      <MandatoryFieldsNote />
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="product-id-field"
            hideErrorMessage
            isRequired
            label={t('prebookingForm.stay.productLabel')}
            name="productId"
            options={productOptions}
            placeholder={t('prebookingForm.stay.productPlaceholder')}
            type="select"
          />
          {minAuthorizedAge ? <Notification className="px-0 py-0" text={minAuthorizedAge} /> : null}
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="departure-city-field"
            hideErrorMessage
            isDisabled={!productId}
            isRequired
            label={t('prebookingForm.stay.departureCityLabel')}
            name="departureCity"
            options={transportOptions}
            placeholder={t('prebookingForm.stay.departureCityPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="departure-date-field"
            dateFormat={dateFormat}
            hideErrorMessage
            isDisabled={!productId}
            isRequired
            label={t('prebookingForm.stay.departureDateLabel')}
            language={language}
            maxDate={maxDate}
            minDate={minDate}
            disabledDays={disabledDays}
            name="departureDate"
            placeholder={t('prebookingForm.stay.departureDatePlaceholder')}
            type="datepicker"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="duration-field"
            hideErrorMessage
            isDisabled={!productId}
            isRequired
            label={t('prebookingForm.stay.durationLabel')}
            name="duration"
            options={durationOptions}
            placeholder={t('prebookingForm.stay.durationPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <p
          className={classNames('w-full mt-2 mb-0 px-2 text-base font-bold', {
            'text-gray-darker': !!productId,
            'text-gray-light': !productId,
          })}
        >
          {t('prebookingForm.stay.accommodationsLabel')}
          {'*'}
        </p>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="accommodation-category-field"
            hideErrorMessage
            isDisabled={!productId}
            isRequired
            name="accommodationCategory"
            options={accommodationCategoryOptions}
            placeholder={t('prebookingForm.stay.accommodationsCategoryPlaceholder')}
            type="select"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="accommodation-field"
            hideErrorMessage
            isDisabled={!productId}
            name="accommodation"
            options={accommodationOptions}
            placeholder={t('prebookingForm.stay.accommodationsTypePlaceholder')}
            type="select"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <p className="w-full mt-2 mb-0 px-2 text-base font-bold">
          {t('prebookingForm.stay.numberOfParticipantsLabel')}
          {'*'}
        </p>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="adults-number-field"
            hideErrorMessage
            name="adultsNumber"
            options={adultsOptions}
            onChange={handleAdultsNumber}
            type="select"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="kids-number-field"
            hideErrorMessage
            name="kidsNumber"
            options={kidsOptions}
            onChange={handleKidsNumber}
            type="select"
          />
        </div>
      </div>
      <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
        {t('prebookingForm.mainTraveler.title')}
      </Title>
      <MandatoryFieldsNote />
      <div className="flex flex-wrap">
        <div className="w-full">
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="traveler-civility-field"
              hideErrorMessage
              isDisabled={hasUser}
              isRequired
              label={t('prebookingForm.mainTraveler.civilityPlaceholder')}
              name="traveler.civility"
              options={civilities.adults}
              placeholder={t('prebookingForm.mainTraveler.civilityPlaceholder')}
              type="select"
            />
          </div>
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-firstName-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.firstNamePlaceholder')}
            name="traveler.firstName"
            placeholder={t('prebookingForm.mainTraveler.firstNamePlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-lastName-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.lastNamePlaceholder')}
            name="traveler.lastName"
            placeholder={t('prebookingForm.mainTraveler.lastNamePlaceholder')}
            type="text"
          />
        </div>
        {hasUnicodeFields && (
          <Fragment>
            <div className="w-full md:w-1/2 p-2">
              <Field
                dataTestId="traveler-firstNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                name="traveler.firstNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
                type="text"
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <Field
                dataTestId="traveler-lastNameUnicode-field"
                hideErrorMessage
                isDisabled={hasUser}
                isRequired
                label={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                name="traveler.lastNameUnicode"
                placeholder={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
                type="text"
              />
            </div>
          </Fragment>
        )}
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-birthdate-field"
            hideErrorMessage
            isDisabled={hasUser}
            label={t('prebookingForm.mainTraveler.birthDateInformation')}
            name="traveler.birthdate"
            placeholder={t('prebookingForm.mainTraveler.birthDateInformation')}
            type="birthdate"
            dayPlaceholder="DD"
            monthPlaceholder="MM"
            yearPlaceholder="YYYY"
            dateFormat={dateFormat}
          />
        </div>
        {!hasSeller && (
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="traveler-gm-number-field"
              hideErrorMessage
              isDisabled={hasUser}
              label={t('prebookingForm.mainTraveler.gmNumberPlaceholder')}
              name="traveler.memberNumber"
              placeholder={t('prebookingForm.mainTraveler.gmNumberPlaceholder')}
              type="text"
            />
          </div>
        )}
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-email-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.emailPlaceholder')}
            name="traveler.email"
            placeholder={t('prebookingForm.mainTraveler.emailPlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full md:w-1/2 p-2">
          <Field
            dataTestId="traveler-phone-field"
            hideErrorMessage
            isDisabled={hasUser}
            isRequired
            label={t('prebookingForm.mainTraveler.phoneNumberPlaceholder')}
            name="traveler.phone"
            placeholder={t('prebookingForm.mainTraveler.phoneNumberPlaceholder')}
            type="text"
          />
        </div>
        {agencies.length > 0 && (
          <div className="w-full md:w-1/2 p-2">
            <Field
              dataTestId="traveler-agency-field"
              hideErrorMessage
              isDisabled={hasSeller}
              label={t('prebookingForm.mainTraveler.favoriteTravelAgencyLabel')}
              name="agency"
              options={agencies}
              placeholder={t('prebookingForm.mainTraveler.favoriteTravelAgencyPlaceholder')}
              type="select"
            />
          </div>
        )}
        {hasAddtionnalTravelers && (
          <div className="w-full p-2">
            <Title className="mt-8 mb-1 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
              {t('prebookingForm.pax.title')}
            </Title>
            <MandatoryFieldsNote />
            {adults.map((adult, index) => (
              <PrebookingFormCompanion
                key={`adult-${index}`}
                companionGroup={ADULTS}
                dateFormat={dateFormat}
                civilities={civilities.adults}
                hasUnicodeFields={hasUnicodeFields}
                index={index}
              />
            ))}
            {kids.map((kid, index) => (
              <PrebookingFormCompanion
                key={`kid-${index}`}
                companionGroup={KIDS}
                dateFormat={dateFormat}
                civilities={civilities.kids}
                hasUnicodeFields={hasUnicodeFields}
                index={index}
              />
            ))}
          </div>
        )}
        {!hasSeller && (
          <div className="w-full p-2">
            <p className="w-full mb-2 text-base font-bold">
              {t('prebookingForm.mainTraveler.optinCommunicationsInformation')}
            </p>
            {optinEmail && (
              <Field
                className="mb-3"
                dataTestId="optin-email-field"
                label={optinEmail.title}
                name="optinEmail"
                optimized
                type="checkbox"
              />
            )}
            {optinPhone && (
              <Field
                className="mb-3"
                dataTestId="optin-phone-field"
                label={optinPhone.title}
                name="optinPhone"
                optimized
                type="checkbox"
              />
            )}
            {optinSms && (
              <Field
                className="mb-3"
                dataTestId="optin-sms-field"
                label={optinSms.title}
                name="optinSms"
                optimized
                type="checkbox"
              />
            )}
            {optinEmail &&
              optinEmail.description && (
                <Text className="mt-4" theme="text-sans-2" text={optinEmail.description} />
              )}
            {optinProfile &&
              optinProfile.description && (
                <Text className="mt-4" theme="text-sans-2" text={optinProfile.description} />
              )}
          </div>
        )}
      </div>
      <div className="mt-4 px-2 text-right">
        <Button
          colorBg={COLORS.BLUE}
          colorBorder={COLORS.BLUE}
          dataTestId="prebooking-submit"
          isDisabled={!isValid}
          isLoading={isSubmitting}
          type="submit"
        >
          {t('prebookingForm.sendRequest')}
        </Button>
      </div>
    </Form>
  );
}
